import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import MembershipCardFormDialog from './MembershipCardFormDialog';
import { membershipCardsService } from '../../services/membershipCards';
import { membershipOrganizationsService } from '../../services/membershipOrganizations';
import { formatDate } from './membership-format';
import type { MembershipCard, MembershipOrganization } from '../../types/membership';

interface MembershipCardsSectionProps {
  gymSlug: string;
  gymName: string;
  memberId: number;
}

/**
 * The tessere a gym holds for one member.
 *
 * The list is wider than what this gym entered: a card from a national body
 * belongs to the climber, so the ones held on their other rosters show up too,
 * locked. Without them, a gym told "this climber already has a FASI card" would
 * have nothing to look at.
 */
export default function MembershipCardsSection({
  gymSlug,
  gymName,
  memberId,
}: MembershipCardsSectionProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [cards, setCards] = useState<MembershipCard[]>([]);
  const [organizations, setOrganizations] = useState<MembershipOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MembershipCard | null>(null);
  /** Id of the card whose accept/reject request is in flight, if any. */
  const [deciding, setDeciding] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedCards, loadedOrganizations] = await Promise.all([
        membershipCardsService.list(gymSlug, memberId),
        membershipOrganizationsService.list(gymSlug),
      ]);
      setCards(loadedCards);
      setOrganizations(loadedOrganizations);
    } catch (error) {
      console.error('Failed to load membership cards:', error);
      toast({ title: t('gymAdmin.members.membership.loadError'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [gymSlug, memberId, t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (card: MembershipCard) => {
    setEditing(card);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    setDialogOpen(false);
    load();
  };

  // A national card another gym entered: this gym says whether it treats it as
  // cover. Reversible, so an accepted card still offers the way back.
  const handleDecision = async (card: MembershipCard, verdict: 'accept' | 'reject') => {
    setDeciding(card.id);
    try {
      const decided = await membershipCardsService.decide(gymSlug, memberId, card.id, verdict);
      setCards((prev) => prev.map((row) => (row.id === decided.id ? decided : row)));
    } catch (error) {
      toast({
        title: t('gymAdmin.members.membership.decisionError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setDeciding(null);
    }
  };

  const handleDelete = async (card: MembershipCard) => {
    const confirmed = window.confirm(
      t('gymAdmin.members.membership.confirmDelete', { number: card.number })
    );
    if (!confirmed) return;

    try {
      await membershipCardsService.remove(gymSlug, memberId, card.id);
      setCards((prev) => prev.filter((row) => row.id !== card.id));
      toast({ title: t('gymAdmin.members.membership.deleted') });
    } catch (error) {
      toast({
        title: t('gymAdmin.members.membership.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-medium">{t('gymAdmin.members.membership.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('gymAdmin.members.membership.subtitle')}
            </p>
          </div>
          <Button size="sm" onClick={openNew}>
            <Plus className="mr-2 size-4" />
            {t('gymAdmin.members.membership.new')}
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-20 w-full" />
        ) : cards.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('gymAdmin.members.membership.empty')}
          </p>
        ) : (
          <ul className="divide-y">
            {cards.map((card) => (
              <li key={card.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{card.organization?.name}</span>
                    <span className="text-sm text-muted-foreground">{card.number}</span>
                    {card.kind && <Badge variant="outline">{card.kind}</Badge>}
                    {card.expired && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="size-3" />
                        {t('gymAdmin.members.membership.expired')}
                      </Badge>
                    )}
                    {!card.editable && (
                      <Badge
                        variant="secondary"
                        className="gap-1"
                        title={t('gymAdmin.members.membership.heldElsewhereHint')}
                      >
                        <Lock className="size-3" />
                        {card.held_by
                          ? t('gymAdmin.members.membership.heldBy', { gym: card.held_by.name })
                          : t('gymAdmin.members.membership.heldElsewhere')}
                      </Badge>
                    )}
                    {card.acceptance_status === 'accepted' && (
                      <Badge variant="outline" className="gap-1">
                        <Check className="size-3" />
                        {t('gymAdmin.members.membership.accepted')}
                      </Badge>
                    )}
                    {card.acceptance_status === 'rejected' && (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <X className="size-3" />
                        {t('gymAdmin.members.membership.rejected')}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {card.expires_on
                      ? t('gymAdmin.members.membership.expiresOn', {
                          date: formatDate(card.expires_on),
                        })
                      : t('gymAdmin.members.membership.noExpiry')}
                    {/* Why the date above is not in the member's expiry column. */}
                    {!card.counts_toward_expiry && (
                      <> · {t('gymAdmin.members.membership.notCounted')}</>
                    )}
                  </p>

                  {card.acceptance_status === 'pending' && (
                    <p className="mt-2 text-sm">{t('gymAdmin.members.membership.decideHint')}</p>
                  )}
                </div>

                {card.editable ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(card)}
                      aria-label={t('common.edit')}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDelete(card)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                ) : (
                  // Only the verdict the card is not already under: an accepted
                  // card offers "reject" and vice versa, so the state is never
                  // ambiguous.
                  <div className="flex shrink-0 items-center gap-2">
                    {card.acceptance_status !== 'accepted' && (
                      <Button
                        size="sm"
                        disabled={deciding === card.id}
                        onClick={() => handleDecision(card, 'accept')}
                      >
                        {deciding === card.id && <Loader2 className="mr-2 size-4 animate-spin" />}
                        {t('gymAdmin.members.membership.accept')}
                      </Button>
                    )}
                    {card.acceptance_status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={deciding === card.id}
                        onClick={() => handleDecision(card, 'reject')}
                      >
                        {t('gymAdmin.members.membership.reject')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <MembershipCardFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        gymSlug={gymSlug}
        gymName={gymName}
        memberId={memberId}
        organizations={organizations}
        card={editing}
        onSaved={handleSaved}
      />
    </Card>
  );
}
